import { Component, Element, Event, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core'
import type { EventEmitter } from '@stencil/core'

import { PageConfig, pageConfigs } from '../../config/page-configs/page-configs'
import { uuid } from '../../utils/uuid'
import { Backend, MessageParams } from '../../utils/backend/backend'
import {
    ConversationCookieLink,
    ConversationDropdown,
    ConversationLink,
    CookieButton,
    CookieLink,
    CookiesAnswerBy,
    Dropdown,
    FormInput,
    FormDropdown,
    LinkSuggestion,
    ReplyParsed,
    Suggestions,
} from '../../utils/backend/reply-parsed'
import type { AnswerBy, Conversation } from '../../utils/backend/reply-parsed'
import { FeatureButton } from '../../config/feature-buttons'
import { matomo } from '../../utils/matomo/matomo'
import { FeedbackEvent } from './c-dialog/c-conversation/c-feedback/c-feedback'
import disableBot from '../../disable-bot'
import { mesosConfig } from '../../config/mesos-config'
import type { BotId } from '../../global/models/models'
import {
    FLOATING_BUTTON_LABEL,
    FORM_THEME_STAMP,
    NEGATIVE_FEEDBACK_PAYLOAD,
    POSITIVE_FEEDBACK_PAYLOAD,
    USER_CHANGES_THEME,
} from '../../global/variables'
import { pageConfigDZT } from '../../config/page-configs/page-config-ids/dzt'
import { OfflineBackendFA } from '../../utils/offline-backend/offline-backend'
import { getCookie } from '../../utils/cookies/cookies'

@Component({
    tag: 'ba-chatbot',
    styleUrl: 'ba-chatbot.scss',
    shadow: true,
})
export class BaChatbot {
    [x: string]: any

    @Prop() jwt: string
    @Prop() clientID: string
    @Prop() pageId: string
    @Prop() alertAllowed?: boolean
    @Event({
        eventName: 'chatbot-open',
    })
    chatbotOpenend: EventEmitter
    @Event({
        eventName: 'chatbot-close',
    })
    chatbotClosed: EventEmitter
    @Event({
        eventName: 'chatbot-restart',
    })
    chatbotRestarted: EventEmitter
    @State() answerBy: AnswerBy
    @State() conversation: Conversation = []
    @State() loading = true
    @State() timeout: boolean
    @State() error: boolean
    @State() showWelcome = true
    @State() isOpen = false
    @State() isAlertOpen = false
    @Element() hostElement: HTMLElement
    pageConfig: PageConfig
    backend = new Backend()
    OfflineBackendFA = new OfflineBackendFA()
    matomo = matomo
    @State() botId: BotId | undefined
    sessionId = uuid()
    conversationId: string
    floatingButton: HTMLButtonElement
    openedBy?: HTMLElement
    dialogElement: HTMLElement
    devEnv = process.env.NODE_ENV
    isLocal = this.devEnv === "development"

    constructor() {
        this.closeChatbotOnEsc = this.closeChatbotOnEsc.bind(this)
        this.pageConfig = pageConfigs[this.getDZTKonf(this.pageId)]
    }

    @Watch('pageId')
    changePageConfig(pageId) {
        this.restart()
        this.pageConfig = pageConfigs[this.getDZTKonf(pageId)]
    }

    @Method()
    async toggle(openedBy?: HTMLElement) {
        // This method is used (only) in the angular demo and in e2e tests: do not delete it.
        if (this.isOpen) {
            this.close()
        } else {
            this.open(openedBy)
        }
    }

    @Method()
    async open(openedBy?: HTMLElement) {
        if (openedBy && typeof (openedBy as any).getAttribute === "function") {
            this.openedBy = openedBy
        }
        this.isOpen = true
        this.chatbotOpenend.emit()

        document.addEventListener('keydown', this.closeChatbotOnEsc)
    }

    @Method()
    async close() {
        this.isOpen = false
        this.chatbotClosed.emit()
        if (this.openedBy) {
            this.openedBy.focus()
        }
        document.removeEventListener('keydown', this.closeChatbotOnEsc)
    }

    @Method()
    async restart() {
        this.showWelcome = true
        this.botId = undefined
        this.conversation = []
        this.answerBy = undefined
        this.chatbotRestarted.emit()
    }

    @Listen('restart')
    handleRestart() {
        this.restart()
    }

    @Listen('close')
    handleClose() {
        this.close()
    }

    @Listen('featureButton')
    handleFeatureButton(e) {
        const featureButton: FeatureButton = e.detail
        this.startConversation(featureButton)
    }

    @Listen('inputText')
    handleInputText(e: CustomEvent) {
        const freeText = e.detail
        if (!freeText) {
            return
        }
        // Add Suggestion Links to the conversation if applicable
        if (this.answerBy?.type === 'suggestions') {
            this.addSuggestionLinksToConversation()
        }
        // Add Dropdown to the conversation if applicable
        if (this.answerBy?.type === 'dropdown') {
            this.addDropdownToConversation(undefined)
        }
        // escape < und > wegen XSS
        this.conversation = [
            ...this.conversation,
            {
                type: 'userMessage',
                text: freeText.replace(/</g, '&lt;').replace(/>/g, '&gt;'),
            },
        ]

        if (this.botId === 'FORM') {
            // Add Form Input to the conversation if applicable
            if (this.answerBy?.type === 'formInput') {
                this.addFormInputToConversation()
            }

            // Add Dropdown to the conversation if applicable
            if (this.answerBy?.type === 'formDropdown') {
                this.addDropdownToForm(undefined)
            }

            // use offline communication for form assistant
            const lang = this.pageId === 'form/form-assistant-ua' ? 'ua' : 'de'
            this.sendOfflineMessage(freeText, lang)
        } else {
            // common usage: sending message to backend
            this.sendMessage({
                message: freeText,
                sessionId: this.sessionId,
                conversationId: this.conversationId,
                botId: this.botId,
                clientID: this.clientID,
            })
        }
    }

    @Listen('inputSalary')
    handleInputSalary(e: CustomEvent) {
        let salary = e.detail
        if (salary === '') {
            salary = 0
        }
        this.conversation = [...this.conversation, { type: 'userMessage', text: `${salary}€` }]
        this.sendMessage({
            message: String(salary),
            sessionId: this.sessionId,
            conversationId: this.conversationId,
            botId: this.botId,
            clientID: this.clientID,
        })
    }

    @Listen('choiceSelected')
    handleChoiceSelected(e: CustomEvent) {
        const { title, payload } = e.detail
        this.conversation = [...this.conversation, { type: 'userMessage', text: title }]

        this.sendMessage({
            message: String(payload),
            sessionId: this.sessionId,
            conversationId: this.conversationId,
            botId: this.botId,
            clientID: this.clientID,
        })
    }

    @Listen('dropdownOptionSelected')
    handleDropdownSelection(e: CustomEvent) {
        const { title, payload } = e.detail
        this.addDropdownToConversation(title)
        this.conversation = [...this.conversation, { type: 'userMessage', text: title }]
        this.sendMessage({
            message: String(payload),
            sessionId: this.sessionId,
            conversationId: this.conversationId,
            botId: this.botId,
            clientID: this.clientID,
        })
    }

    @Listen('linkSuggestionSelected')
    handleLinkSuggestionSelected(e: CustomEvent) {
        const link: LinkSuggestion = e.detail
        // If the are button and link suggestions mixed and a link with no payload is clicked, the conversation will not progress and only the link opens
        if (!link.payload) {
            return
        }
        this.addSuggestionLinksToConversation()

        this.sendMessage({
            message: String(link.payload),
            sessionId: this.sessionId,
            conversationId: this.conversationId,
            botId: this.botId,
            clientID: this.clientID,
        })
    }

    @Listen('buttonSuggestionSelected')
    handleButtonSuggestionSelected(e: CustomEvent) {
        // If we have mixed button and link suggestions we want to keep all links, but only the selected button
        this.addSuggestionLinksToConversation()
        const { title, payload } = e.detail
        this.conversation = [...this.conversation, { type: 'userMessage', text: title }]

        if (this.botId === 'FORM') {
            // Add Form Input to the conversation if applicable
            if (this.answerBy?.type === 'formInput') {
                this.addFormInputToConversation()
            }

            // use offline communication for form assistant
            const lang = this.pageId === 'form/form-assistant-ua' ? 'ua' : 'de'
            this.sendOfflineMessage(payload, lang)
        } else {
            // common usage: sending message to backend
            this.sendMessage({
                message: String(payload),
                sessionId: this.sessionId,
                conversationId: e.detail.changeBotTheme ? uuid() : this.conversationId,
                botId: e.detail.changeBotTheme ? e.detail.botId : this.botId,
                clientID: this.clientID,
            })
        }
    }

    @Listen('cookieButtonSelected')
    handleCookieButtonSelected(e: CustomEvent) {
        this.handleCookieLinkSelected(e)
    }

    @Listen('cookieLinkSelected')
    handleCookieLinkSelected(e: CustomEvent) {
        const cookieLink: CookieLink | CookieButton = e.detail
        // If the are button and link suggestions mixed and a link with no payload is clicked, the conversation will not progress and only the link opens
        if (!cookieLink.payload) {
            return
        }
        // If a link has a payload it means the backend wants to answer with new messages. There might be more than one link and we want to
        // be able to click them later, so we will add them to the conversation, while removing any buttons.
        const cookieLinks: CookieLink[] = (this.answerBy as CookiesAnswerBy).elements.filter(
            (element): element is CookieLink => element.type === 'link'
        )
        const conversationCookieLinks: ConversationCookieLink[] = cookieLinks.map((cl) => {
            return {
                type: 'conversationCookieLink',
                text: cl.text,
                screenReader: cl.screenReader,
                url: cl.url,
                cookies: cl.cookies,
            }
        })
        this.addToConversation(conversationCookieLinks)

        this.sendMessage({
            message: String(cookieLink.payload),
            sessionId: this.sessionId,
            conversationId: this.conversationId,
            botId: this.botId,
            clientID: this.clientID,
        })
    }

    @Listen('themeChanged')
    handleThemeChange() {
        let userMessage: any
        if (this.botId === 'FORM') {
            userMessage = FORM_THEME_STAMP
            this.conversation = [...this.conversation, { type: 'userMessage', text: userMessage }]
            this.botId = 'FORM'
            this.handleRestart()
        } else {
            // Add Suggestion Links to the conversation if applicable
            if (this.answerBy?.type === 'suggestions') {
                this.addSuggestionLinksToConversation()
            }
            userMessage = USER_CHANGES_THEME
            this.conversation = [...this.conversation, { type: 'userMessage', text: userMessage }]
            this.botId = 'STARTSEITE'
            this.sendMessage({
                message: '/change_topic_to_startseite',
                sessionId: this.sessionId,
                conversationId: this.conversationId,
                botId: this.botId,
                clientID: this.clientID,
            })
        }
    }

    @Listen('feedback')
    handleFeedback({ detail: feedback }: { detail: FeedbackEvent }) {
        const numRating = feedback.rating
        switch (numRating) {
            case 1:
            case -1: {
                const feedbackPayload = numRating === 1 ? POSITIVE_FEEDBACK_PAYLOAD : NEGATIVE_FEEDBACK_PAYLOAD
                this.conversation = [...this.conversation, { type: 'feedbackGivenStamp' }]
                this.sendMessage({
                    message: feedbackPayload,
                    sessionId: this.sessionId,
                    conversationId: this.conversationId,
                    botId: this.botId,
                    clientID: this.clientID,
                })
                break
            }
            case 0:
                if (feedback.withdrawn) {
                    this.conversation = [...this.conversation, { type: 'feedbackWithdrawnStamp' }]
                    // In case of withdrawing negative feedback, we need also to remove the suggestion buttons
                    // related to it, by changing the type of answerBy
                    this.answerBy = { type: 'textInput' }
                }
                break
        }
    }

    addSuggestionLinksToConversation() {
        // If a link has a payload it means the backend wants to answer with new messages. There might be more than one link and we want to
        // be able to click them later, so we will add them to the conversation, while removing any buttons.
        const links: LinkSuggestion[] = (this.answerBy as Suggestions).elements.filter(
            (element): element is LinkSuggestion => element.type === 'link'
        )
        const conversationLinks: ConversationLink[] = links.map((l) => {
            return {
                type: 'conversationLink',
                title: l.title,
                screenReader: l.screenReader,
                url: l.url,
                fullWidth: l.fullWidth,
            }
        })
        this.addToConversation(conversationLinks)
    }

    addDropdownToConversation(selectedOption: string | undefined) {
        const conversationDropdown: ConversationDropdown = {
            ...(this.answerBy as Dropdown),
            type: 'conversationDropdown',
            selectedOptionLabel: selectedOption,
        }
        this.conversation = [...this.conversation, conversationDropdown]
    }

    addDropdownToForm(selectedOption: string | undefined) {
        const formDropdown: FormDropdown = {
            ...(this.answerBy as Dropdown),
            type: 'formDropdown',
            selectedOptionLabel: selectedOption,
        }
        this.conversation = [...this.conversation, formDropdown]
    }

    addFormInputToConversation() {
        const formInput: FormInput = {
            ...(this.answerBy as FormInput),
            type: 'formInput',
        }
        this.conversation = [...this.conversation, formInput]
    }

    @Watch('botId')
    handleBotIdChange(botId: BotId) {
        this.conversationId = uuid()
        // Add 'start theme' stamp text to conversation
        if (botId !== 'STARTSEITE') {
            this.conversation = [...this.conversation, { type: 'startThemeStamp', botId }]
        }
    }

    getDZTKonf(pageId) {
        let checkPageId = pageId || ''
        checkPageId = checkPageId.toLowerCase()
        if (checkPageId.indexOf(mesosConfig.dztCmsPath) !== -1) {
            // das muss später geändert werden
            // cms Seiten mit 'unternehmen' und 'institutionen' sind von der anzeige ausgeschlossen
            if (pageConfigDZT.whitelist?.some((arrayElement) => checkPageId.indexOf(arrayElement) !== -1)) {
                checkPageId = 'vor-ort'
            }
        }
        return checkPageId
    }

    componentDidLoad() {
        const modal = document.querySelector('bahf-cd-modal')

        if (!modal) {
            this.enableMonitoring()
            return
        }

        const oberserver = new MutationObserver(() => {
            const stillThere = document.querySelector('bahf-cd-modal')
            if (!stillThere) {
                oberserver.disconnect()
                this.enableMonitoring()
            }
        })

        oberserver.observe(document.body, { childList: true, subtree: true })
    }

    enableMonitoring = () => {
        if (getCookie('cookie_consent') === 'accepted') {
            // console.log("Cookie Constent", true)
            this.loadMatomo()
        } else if (process.env.NODE_ENV === 'development') {
            // console.log("Dev - No Cookie Constent", true)
            this.loadMatomo()
        } else if (window.location.host === "localhost") {
            // console.log("Dev - Localhost", true)
            this.loadMatomo()
        } else {
            // console.log("No Cookie Constent", false)
            return
        }
    }

    loadMatomo() {
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.async = true
        script.src = mesosConfig.matomoTrackingURL
        document.body.appendChild(script)
    }

    addToConversation(newConversation: Conversation) {
        this.conversation = [...this.conversation, ...newConversation]
    }

    startConversation({ botId, message }: { botId: BotId; message: string }) {
        this.showWelcome = false
        this.conversationId = uuid()
        this.botId = botId
        if (this.botId === 'FORM') {
            // Add Form Input to the conversation if applicable
            if (this.answerBy?.type === 'formInput') {
                this.addFormInputToConversation()
            }

            // use offline communication for form assistant
            const lang = this.pageId === 'form/form-assistant-ua' ? 'ua' : 'de'
            this.sendOfflineMessage(message, lang)
        } else {
            // start common conversation with backend
            this.sendMessage({
                sessionId: this.sessionId,
                conversationId: this.conversationId,
                botId,
                message,
                clientID: this.clientID,
            })
        }
    }

    sendOfflineMessage(messageKey: string, lang: string) {
        this.answerBy = undefined
        let response
        if (this.botId === 'FORM') {
            response = this.OfflineBackendFA.message(messageKey, lang)
        }
        this.botId = response.botId
        this.loading = false
        this.answerBy = response.answerBy
        this.addToConversation(response.messages)
    }

    sendMessage = async (messageParams: MessageParams) => {
        try {
            this.loading = true
            this.answerBy = { type: 'textInput' }
            this.timeout = false

            // Set a timeout for the messagePromise
            const timeoutDuration = 100000 // Adjust the timeout duration as needed (e.g., 20000 milliseconds or 20 seconds)
            const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => {
                    reject(new Error('Request timed out'))
                }, timeoutDuration)
            })

            // Create a promise that resolves when the backend.message() call completes
            const messagePromise: Promise<ReplyParsed> = this.backend.message(messageParams)

            // Use Promise.race to wait for either the messagePromise or the timeoutPromise
            const response = await Promise.race([messagePromise, timeoutPromise])

            // Assuming the response is of type ReplyParsed
            const { messages, answerBy, botId, status } = response
            // Check the HTTP status code (assuming backend provides status code directly)
            if (status && status >= 200 && status < 300) {
                // Status code is in the success range
                if (botId) {
                    this.botId = botId
                }
                this.loading = false
                this.timeout = false
                this.answerBy = answerBy
                this.addToConversation(messages)
            } else {
                this.loading = false
                this.timeout = true
                // Handle HTTP error status codes or missing status
                throw new Error(`Invalid HTTP response or missing status code: ${status}`)
            }
        } catch (e) {
            // Check if the error is due to a timeout
            if (e.message === 'Request timed out') {
                this.loading = false
                this.timeout = true
                this.error = e
            }
        }
    }

    closeChatbotOnEsc(e: any) {
        if (e.key === 'Escape') {
            const botHasFocus = this.isFocusInBot()
            if (botHasFocus) {
                this.close()
            }
        }
    }

    isFocusInBot = () => {
        return this.dialogElement.shadowRoot!.contains(document.activeElement?.shadowRoot?.activeElement?.shadowRoot?.activeElement || null)
    }

    handleAlertUserConfirm() {
        this.open(this.floatingButton)
    }

    handleAlertOpenChange({ detail }: { detail: boolean }) {
            this.isAlertOpen = detail
    }

    render() {
        if (!this.pageConfig) {
            return null
        }
        // Future Task Export id to keep in sync with c-dialog
        return (
            <Host hidden={disableBot(mesosConfig, window.location.host, window.location.pathname)}>
                <c-alert
                    class="noPrint"
                    pageConfig={this.pageConfig}
                    isDialogOpen={this.isOpen}
                    onAlertOpenChange={this.handleAlertOpenChange.bind(this)}
                    onUserConfirm={this.handleAlertUserConfirm.bind(this)}
                ></c-alert>
                {/*
                Future Task Set aria-hidden=true? Not for now.
                Future Task Add nice animation for the floating button, because there is a delay in prod until it appears
                */}
                <button
                    title={FLOATING_BUTTON_LABEL}
                    tabindex={this.isAlertOpen ? -1 : 0}
                    id="cb-floating-button"
                    aria-label={FLOATING_BUTTON_LABEL}
                    aria-expanded={this.isOpen ? 'true' : 'false'}
                    aria-controls="cb-digitaler-assistent"
                    aria-hidden={this.isAlertOpen ? 'true' : 'false'}
                    data-analytics-track="true"
                    data-analytics-action="ButtonClicks TCB: Klick"
                    data-analytics-value="Open Chatbot"
                    data-analytics-name="TCB: Avatar Button"
                    data-analytics-category="TCB | ButtonClicks"
                    onClick={() => (this.isOpen ? this.close() : this.open(this.floatingButton))}
                    ref={(el) => (this.floatingButton = el!)}
                    class={`noPrint ${this.isAlertOpen || this.isOpen ? 'hide-floating-button' : ''}`}
                >
                    <c-avatar-bot variant="button" class="noPrint"></c-avatar-bot>
                    <div id="cb-tip" class="triangle noPrint"></div>
                    <div id="cb-triangle-focus" class="triangle noPrint"></div>
                </button>
                <c-dialog
                    isOpen={this.isOpen}
                    loading={this.loading}
                    timeout={this.timeout}
                    showWelcome={this.showWelcome}
                    pageConfig={this.pageConfig}
                    data-analytics-track={true}
                    conversation={this.conversation}
                    answerBy={this.answerBy}
                    currentBotId={this.botId}
                    ref={(el) => (this.dialogElement = el!)}
                    isLocal={this.isLocal}
                ></c-dialog>
            </Host>
        )
    }
}
